import { useState } from 'react';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, InputAdornment, TextField } from '@mui/material';

const PRIMARY = '#0d8392';

/**
 * Shared toolbar for all reports.
 * Props:
 *  title, search, onSearchChange, fromDate, onFromDate, toDate, onToDate,
 *  onRefresh, loading, onExport, exportDisabled, searchPlaceholder, onBack
 */
const ReportToolbar = ({
  title,
  search,
  onSearchChange,
  fromDate,
  onFromDate,
  toDate,
  onToDate,
  onRefresh,
  loading,
  onExport,
  exportDisabled,
  searchPlaceholder = 'Search...',
  onBack,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);

  const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '8px',
    height: 29,
    width: 29,
    color: '#fff',
    flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
  };

  const dateInput = {
    height: 29,
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 8,
    padding: '0 8px',
    fontSize: 12,
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    colorScheme: 'dark',
    flexShrink: 0,
  };

  return (
    <div className="report-toolbar" style={{
      display: 'flex',
      alignItems: 'center',
      padding: '6px 12px',
      background: `linear-gradient(135deg, ${PRIMARY} 0%, #1aabb9 100%)`,
      gap: 6,
      flexWrap: 'nowrap',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
    }}>
      {/* Back + Title */}
      <Tooltip title="Back to Reports" arrow>
        <IconButton onClick={onBack} size="small" sx={iconBtn}>
          <ArrowBack sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Title — always visible */}
      <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {title}
      </span>

      {/* Spacer */}
      <div style={{ flex: 1, minWidth: 4 }} />

      {/* Search expand/collapse */}
      {searchOpen ? (
        <>
          <TextField
            autoFocus
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}>
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flexShrink: 0,
              width: 180,
              '& .MuiOutlinedInput-root': {
                height: 29, fontSize: 12, borderRadius: '8px', color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
              '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 },
            }}
          />
          <Tooltip title="Close search" arrow>
            <IconButton size="small" onClick={() => { setSearchOpen(false); onSearchChange(''); }} sx={iconBtn}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Tooltip title="Search" arrow>
          <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
            <Search sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Date inputs */}
      <input type="date" value={fromDate} onChange={e => onFromDate(e.target.value)} style={dateInput} />
      <input type="date" value={toDate}   onChange={e => onToDate(e.target.value)}   style={dateInput} />

      {/* Refresh */}
      <Tooltip title="Refresh" arrow>
        <IconButton onClick={onRefresh} disabled={loading} size="small" sx={iconBtn}>
          <Refresh sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Export */}
      <Tooltip title="Export Excel" arrow>
        <IconButton onClick={onExport} disabled={exportDisabled} size="small" sx={iconBtn}>
          <GetApp sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default ReportToolbar;

